import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	Layout,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { CarSettings, Checkmark, Close } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
	checkActiveDisclaimer,
	RegisteredDisclaimer,
} from '../../helpers/get-disclaimer';
import {
	getEtiquetaDgt,
	getFinanciacionType,
	useTransformNumberFormat,
} from '../../helpers/ofertas-helper';
import {
	CarlineWithOfertas,
	Equipamiento,
	Oferta,
} from '../../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { RedirectCTAs } from '../redirect-CTAs';
import { Gradient } from './content-layer';
import { ImageSlider } from './image-slider';

const StickyBottomContainer = styled.div<{ gradientType?: Gradient }>`
	background-image: ${(props) => props.gradientType};
	bottom: 0px;
	position: sticky;

	@media screen and (max-width: 960px) {
		text-align: center;
	}

	@media screen and (min-width: 1280px) and (max-width: 1599px) {
		background-color: none;
	}

	@media screen and (min-width: 1280px) {
		z-index: -10;
	}
`;

const DesktopContainer = styled.div`
	position: relative;
`;

const SliderContainer = styled.div`
	@media screen and (max-width: 959px) {
		padding-top: ${(props) => props.theme.size.dynamic0100};
	}

	@media screen and (min-width: 1280px) {
		position: absolute;
		top: 0;
		right: 0;
		padding-top: 0;
		width: 50%;
	}
`;

const CloseDiv = styled.div`
	text-align: end;
	cursor: pointer;
`;

const ScrollContainer = styled.div`
	width: 100%;
	max-height: calc(100vh);
	overflow-y: auto;

	::-webkit-scrollbar {
		display: none; /* for Chrome, Safari and Opera */
		scrollbar-width: none; /* Firefox */
	}
`;

// Not in use at the moment (nov.24)
const PersonalizaCTA = ({
	selectedOferta,
}: {
	selectedOferta: Oferta;
}): React.ReactElement => {
	const trackingManager = useTrackingManager();

	const handleOnClick = () => {
		trackingManager.trackOfertasLinkClick(
			'Personaliza el tuyo',
			{ contentId: 'Personaliza el tuyo' },
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.motorizacionCode?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: getFinanciacionType(selectedOferta),
				PriceRateTotal: selectedOferta?.cuota ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: selectedOferta?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);
	};

	return (
		<Container horizontalAlign={'center'} verticalAlign={'center'}>
			<Text textAlign={TextAlignment.center}>
				<CTA
					tag="a"
					emphasis={'tertiary'}
					icon={<CarSettings />}
					size="large"
					iconPosition="start"
					stretchContent
					onClick={handleOnClick}
				>
					Personaliza el tuyo
				</CTA>
			</Text>
		</Container>
	);
};

const ScrollToSectionButtons = ({
	equipamientoRef,
	precioRef,
	galeriaRef,
}: {
	equipamientoRef: React.RefObject<HTMLElement>;
	precioRef: React.RefObject<HTMLElement>;
	galeriaRef: React.RefObject<HTMLElement>;
}): React.ReactElement => {
	const [selectedSection, setSelectedSection] =
		useState<string>('equipamiento');

	const handleSectionOnClick = (
		sectionName: string,
		sectionRef: React.RefObject<HTMLElement>,
	) => {
		setSelectedSection(sectionName);

		if (sectionRef.current) {
			sectionRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
	};

	return (
		<Container
			data-testid="scroll-to-section__buttons"
			wrap={'never'}
			gutter={ContainerGutter.static250}
			padding={{
				top: ContainerPadding.static400,
			}}
			stretchContent={{
				[Breakpoints.default]: true,
				[Breakpoints.b560]: false,
			}}
		>
			<CTA
				tag="a"
				emphasis={selectedSection === 'equipamiento' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('equipamiento', equipamientoRef)}
			>
				Equipamiento
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'precio' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('precio', precioRef)}
			>
				Precio
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'galeria' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('galeria', galeriaRef)}
			>
				Galería
			</CTA>
		</Container>
	);
};

export const OfertaLayer = ({
	setIsLayerVisible,
	activeCarline,
	selectedOferta,
	layerRef,
	disclaimers,
}: {
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	activeCarline: CarlineWithOfertas;
	layerRef?: React.RefObject<LayerHandleV2<any>>;
	selectedOferta: Oferta;
	disclaimers: RegisteredDisclaimer[];
	className: string;
}): React.ReactElement => {
	const equipamientoRef = useRef<HTMLElement>(null);
	const precioRef = useRef<HTMLElement>(null);
	const galeriaRef = useRef<HTMLElement>(null);

	const trackingManager = useTrackingManager();

	useEffect(() => {
		trackingManager.trackOfertasLayerLoad(
			{ contentId: 'Oferta Layer' },
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.motorizacionCode?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: getFinanciacionType(selectedOferta),
				PriceRateTotal: selectedOferta?.cuota ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: selectedOferta?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);
	}, []);

	const handleCloseOnClick = () => {
		setIsLayerVisible(false);

		trackingManager.trackOfertasLayerClose(
			'Close Oferta Layer',
			{
				contentId: 'Close Oferta Layer',
			},
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.motorizacionCode?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: getFinanciacionType(selectedOferta),
				PriceRateTotal: selectedOferta?.cuota ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: selectedOferta?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);

		if (layerRef?.current) {
			layerRef?.current.close();
			return (layerRef.current = null);
		}
	};

	return (
		<>
			<DesktopContainer className="desktop-container">
				<BreakpointWrapper
					data-testid="layer-close-button"
					min={Breakpoints.b1600}
				>
					<Layout
						data-testid="oferta-layer__layout-close"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 11 },
								{ name: 'a', columns: 1 },
								{ name: '.', columns: 12 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 11 },
								{ name: 'a', columns: 1 },
								{ name: '.', columns: 12 },
							],
						}}
						allowOverflowingContent
					>
						<Container
							padding={{
								top: ContainerPadding.dynamic0100,
								bottom: ContainerPadding.dynamic0300,
							}}
						>
							<Text>
								<CloseDiv
									className="close-button__container"
									onClick={handleCloseOnClick}
								>
									<Text textAlign={TextAlignment.end}>
										<Close />
									</Text>
								</CloseDiv>
							</Text>
						</Container>
					</Layout>
				</BreakpointWrapper>
				<Layout
					data-testid="oferta-layer__layout-info"
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 23 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b560]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 22 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b960]: [
							{ name: '.', columns: 2 },
							{ name: 'a', columns: 21 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b1280]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 12 },
						],
						[Breakpoints.b1600]: [
							{ name: '.', columns: 2 },
							{ name: 'a', columns: 8 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 13 },
						],
						[Breakpoints.b2560]: [
							{ name: '.', columns: 3 },
							{ name: 'a', columns: 6 },
							{ name: '.', columns: 3 },
							{ name: 'b', columns: 12 },
						],
					}}
					allowOverflowingContent
				>
					<ScrollContainer className="scroll-container">
						<Container
							wrap={'always'}
							horizontalAlign={{ [Breakpoints.b1280]: 'space-between' }}
							padding={{
								top: ContainerPadding.dynamic0100,
								left: {
									[Breakpoints.default]: ContainerPadding.grid001,
									[Breakpoints.b560]: ContainerPadding.static0,
								},
							}}
							shrinkContent
						>
							<Container
								data-testid="oferta-layer__header header"
								wrap={'never'}
								horizontalAlign={'space-between'}
								verticalAlign={'flex-start'}
								shrinkContent
							>
								<Container data-testid="header__name-etiqueta" wrap={'always'}>
									<Container
										wrap={'never'}
										gutter={ContainerGutter.static200}
										shrinkContent
									>
										<Text
											tag={TextTag.h1}
											appearance={TokenTextAppearance.headline300}
											bold
										>
											{activeCarline.nuevo ? 'Nuevo ' : null}
											{selectedOferta.carline?.salesgroup.title}
										</Text>
										{selectedOferta.etiquetaDGT && (
											<Text>
												<img
													src={getEtiquetaDgt(selectedOferta.etiquetaDGT)}
													width={32}
													alt={activeCarline.title}
												/>
											</Text>
										)}
									</Container>

									<Container shrinkContent>
										<BreakpointWrapper
											min={Breakpoints.default}
											max={Breakpoints.b1920}
										>
											<Text
												tag={TextTag.h3}
												appearance={TokenTextAppearance.copy100}
												bold
											>
												{selectedOferta.motorizacion}
											</Text>
										</BreakpointWrapper>
										<BreakpointWrapper min={Breakpoints.b1920}>
											<Text
												tag={TextTag.h3}
												appearance={TokenTextAppearance.copy200}
												bold
											>
												{selectedOferta.motorizacion}
											</Text>
										</BreakpointWrapper>
									</Container>
								</Container>

								<BreakpointWrapper
									min={Breakpoints.default}
									max={Breakpoints.b1600}
								>
									<Text>
										<CloseDiv
											className="close-button__container"
											onClick={handleCloseOnClick}
										>
											<Text textAlign={TextAlignment.end}>
												<Close />
											</Text>
										</CloseDiv>
									</Text>
								</BreakpointWrapper>
							</Container>

							<BreakpointWrapper
								min={Breakpoints.default}
								max={Breakpoints.b960}
							>
								<ScrollToSectionButtons
									equipamientoRef={equipamientoRef}
									precioRef={precioRef}
									galeriaRef={galeriaRef}
								/>
							</BreakpointWrapper>
							<Container
								data-testid="oferta-layer__info info"
								padding={{ top: ContainerPadding.static400 }}
								wrap={'always'}
								ref={equipamientoRef}
							>
								<Text
									tag={TextTag.h4}
									appearance={TokenTextAppearance.copy200}
									bold
								>
									Equipamiento destacado
								</Text>
								<Container
									data-testid="info_check-list"
									padding={{ top: ContainerPadding.static250 }}
									wrap={'always'}
								>
									{selectedOferta.equipamientos?.serie
										.slice(0, 7)
										.map((equipamiento: Equipamiento, index) => {
											return (
												<Container
													padding={{ bottom: ContainerPadding.static100 }}
													wrap={'never'}
													verticalAlign={'flex-start'}
													gutter={ContainerGutter.static200}
													shrinkContent
													key={index}
												>
													<Text tag={TextTag.span}>
														<Checkmark variant="small" />
													</Text>
													<BreakpointWrapper
														min={Breakpoints.default}
														max={Breakpoints.b1920}
													>
														<Text
															tag={TextTag.span}
															hyphens="auto"
															appearance={TokenTextAppearance.copy100}
														>
															{equipamiento.equipamiento.titulo}
														</Text>
													</BreakpointWrapper>
													<BreakpointWrapper min={Breakpoints.b1920}>
														<Text
															tag={TextTag.span}
															hyphens="auto"
															appearance={TokenTextAppearance.copy200}
														>
															{equipamiento.equipamiento.titulo}
														</Text>
													</BreakpointWrapper>
												</Container>
											);
										})}
								</Container>
							</Container>
							<Container
								data-testid="oferta-layer__price price"
								padding={{
									top: ContainerPadding.static400,
									bottom: ContainerPadding.static400,
								}}
								wrap={'always'}
								shrinkContent
								ref={precioRef}
							>
								<Text
									tag={TextTag.h4}
									appearance={TokenTextAppearance.copy200}
									bold
								>
									Elige la opción que encaja contigo:
								</Text>
								<Container
									data-testid="price__info"
									wrap={'never'}
									padding={{
										top: ContainerPadding.static250,
									}}
									shrinkContent
								>
									{selectedOferta.precioText && (
										<Container
											gutter={ContainerGutter.static100}
											wrap={'always'}
											padding={{
												right:
													selectedOferta.precioText &&
													ContainerPadding.static300,
											}}
											stretchContent
										>
											<BreakpointWrapper
												min={Breakpoints.default}
												max={Breakpoints.b1920}
											>
												<Container>
													<Text appearance={TokenTextAppearance.copy100}>
														{'Desde '}
														<Text
															tag={TextTag.span}
															appearance={TokenTextAppearance.bigcopy100}
															color={TokenTextColor.active}
															bold
														>
															{selectedOferta.precioText}
														</Text>
													</Text>

													<Text sup bold>
														{checkActiveDisclaimer(
															disclaimers,
															selectedOferta.legal,
														)?.ref?.replace('.', '')}
													</Text>
												</Container>

												<Text
													tag={TextTag.span}
													appearance={TokenTextAppearance.copy100}
												>
													{'Sujeto a financiación lineal'}
												</Text>
											</BreakpointWrapper>
											<BreakpointWrapper min={Breakpoints.b1920}>
												<Container>
													<Text appearance={TokenTextAppearance.copy200}>
														{'Desde '}
														<Text
															tag={TextTag.span}
															appearance={TokenTextAppearance.bigcopy100}
															color={TokenTextColor.active}
															bold
														>
															{selectedOferta.precioText}
														</Text>
													</Text>
													<Text sup bold>
														{checkActiveDisclaimer(
															disclaimers,
															selectedOferta.legal,
														)?.ref?.replace('.', '')}
													</Text>
												</Container>
												<Text
													tag={TextTag.span}
													appearance={TokenTextAppearance.copy200}
												>
													{'Sujeto a financiación lineal'}
												</Text>
											</BreakpointWrapper>
										</Container>
									)}
									<BreakpointWrapper min={Breakpoints.b560}>
										{selectedOferta.precioText && selectedOferta.cuota && (
											<Divider isVertical />
										)}
									</BreakpointWrapper>
									{selectedOferta.cuota && (
										<Container
											gutter={ContainerGutter.static100}
											wrap={'always'}
											shrinkContent
											padding={{
												left:
													selectedOferta.precioText &&
													ContainerPadding.static300,
											}}
										>
											<BreakpointWrapper
												min={Breakpoints.default}
												max={Breakpoints.b1920}
											>
												<Container wrap={'always'}>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.bigcopy100}
														bold
													>
														{selectedOferta && (
															<>
																{parseFloat(selectedOferta.cuota.toFixed(2))
																	.toString()
																	.replace('.', ',')}
																€/mes
																<Text sup bold>
																	{checkActiveDisclaimer(
																		disclaimers,
																		selectedOferta.cuotaLegal,
																	)?.ref?.replace('.', '')}{' '}
																</Text>
																<CTA
																	tag={'a'}
																	emphasis={'tertiary'}
																	href={
																		selectedOferta.financiacionName ===
																		'con-my-way'
																			? 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-way.html'
																			: 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-renting.html'
																	}
																	target={'_blank'}
																>
																	{selectedOferta.financiacionText}
																</CTA>
															</>
														)}
													</Text>

													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy100}
													>{`${selectedOferta.plazos} cuotas`}</Text>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy100}
														hyphens="auto"
													>
														{`${useTransformNumberFormat(selectedOferta.cuotaEntrada)}€ de entrada (IVA incluido)`}
													</Text>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy100}
													>{`Cuota final: ${useTransformNumberFormat(selectedOferta.cuotaFinal)}€`}</Text>
												</Container>
											</BreakpointWrapper>
											<BreakpointWrapper min={Breakpoints.b1920}>
												<Container wrap={'always'}>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.bigcopy100}
														bold
													>
														{selectedOferta && (
															<>
																{parseFloat(selectedOferta.cuota.toFixed(2))
																	.toString()
																	.replace('.', ',')}
																€/mes
																<Text sup bold>
																	{checkActiveDisclaimer(
																		disclaimers,
																		selectedOferta.cuotaLegal,
																	)?.ref?.replace('.', '')}{' '}
																</Text>
																<CTA
																	tag={'a'}
																	emphasis={'tertiary'}
																	href={
																		selectedOferta.financiacionName ===
																		'con-my-way'
																			? 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-way.html'
																			: 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-renting.html'
																	}
																	target={'_blank'}
																>
																	{selectedOferta.financiacionText}
																</CTA>
															</>
														)}
													</Text>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy200}
													>{`${selectedOferta.plazos} cuotas`}</Text>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy200}
														hyphens="auto"
													>
														{`${useTransformNumberFormat(selectedOferta.cuotaEntrada)}€ de entrada (IVA incluido)`}
													</Text>
													<Text
														tag={TextTag.span}
														appearance={TokenTextAppearance.copy200}
													>{`Cuota final: ${useTransformNumberFormat(selectedOferta.cuotaFinal)}€`}</Text>
												</Container>
											</BreakpointWrapper>
										</Container>
									)}
								</Container>
								<BreakpointWrapper
									min={Breakpoints.b960}
									max={Breakpoints.b1280}
								>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
									>
										<RedirectCTAs
											copy={'Pide esta oferta'}
											emphasis="secondary"
											isMobile={true}
											oferta={selectedOferta}
											trackingRef={'layer'}
										/>
									</Container>
								</BreakpointWrapper>
								<BreakpointWrapper min={Breakpoints.b1280}>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
									>
										<RedirectCTAs
											copy={'Pide esta oferta'}
											emphasis="secondary"
											isMobile={false}
											oferta={selectedOferta}
											trackingRef={'layer'}
										/>
									</Container>
								</BreakpointWrapper>
							</Container>
						</Container>
					</ScrollContainer>

					<Container>
						{selectedOferta.galeria && (
							<BreakpointWrapper min={Breakpoints.b1280}>
								<SliderContainer className="slider-container">
									<ImageSlider oferta={selectedOferta} />
								</SliderContainer>
							</BreakpointWrapper>
						)}
					</Container>
				</Layout>

				{selectedOferta.galeria && (
					<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b1280}>
						<Layout
							data-testid="oferta-layer__layout-slider"
							appearance={{
								[Breakpoints.default]: [{ name: 'a', columns: 24 }],
							}}
							allowOverflowingContent
						>
							<SliderContainer className="slider-container" ref={galeriaRef}>
								<ImageSlider oferta={selectedOferta} />
							</SliderContainer>
						</Layout>
					</BreakpointWrapper>
				)}
			</DesktopContainer>

			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				<StickyBottomContainer
					className="sticky-bottom-container"
					gradientType={Gradient.LINEAR_GRADIENT_RIGHT}
				>
					<Layout
						data-testid="oferta-layer__layout-cta-mobile"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 20 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 11 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 8 },
								{ name: '.', columns: 14 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 6 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 12 },
							],
						}}
						allowOverflowingContent
					>
						<Container
							data-testid="cta-container"
							padding={{
								top: ContainerPadding.static250,
								bottom: ContainerPadding.dynamic0100,
							}}
							verticalAlign={'center'}
							horizontalAlign={{
								[Breakpoints.default]: 'center',
								[Breakpoints.b1280]: 'flex-start',
							}}
							wrap={{
								[Breakpoints.default]: 'always',
								[Breakpoints.b960]: 'never',
							}}
							shrinkContent
						>
							<RedirectCTAs
								copy={'Pide esta oferta'}
								emphasis="secondary"
								isMobile={true}
								oferta={selectedOferta}
								trackingRef={'layer'}
							/>
						</Container>
					</Layout>
				</StickyBottomContainer>
			</BreakpointWrapper>
		</>
	);
};
